import classNames from 'clsx';
import RBush from 'rbush';

import { commarize } from 'shared/common/utils/number';
import { BALLOON_HOVERED_Z_INDEX, BALLOON_PROMO_Z_INDEX } from 'shared/map-search/constants/map';
import { EClustersType } from 'shared/map-search/types/clustersType';
import { IRBushItem, type TFeature, type TFeaturePropertiesRequiredPrice } from 'shared/map-search/types/map';

import styles from './BalloonPromoManager.css';
import { formatBalloonCount, getIcon } from '../utils';

interface IBalloonPromoManagerOptions {
  map: YMaps.Map;
  rbush: RBush<IRBushItem>;
  pointType: EClustersType | null;
}

/**
 * @todo разработка в MNZ-700
 * Менеджер для промо-слоя балунов
 * Используется для отображения пинов с пульсацией
 */
export function defineBalloonPromoManager(ymaps: YMaps.IYMaps) {
  if (ymaps.modules.isDefined('BalloonPromoManager')) {
    return;
  }

  ymaps.modules.define<
    [YMaps.ITemplateLayoutFactory, typeof YMaps.shape.Rectangle, typeof YMaps.geometry.pixel.Rectangle]
  >(
    'BalloonPromoManager',
    ['templateLayoutFactory', 'shape.Rectangle', 'geometry.pixel.Rectangle'],
    (
      provide,
      templateLayoutFactory: YMaps.ITemplateLayoutFactory,
      shapeRectangle: typeof YMaps.shape.Rectangle,
      geometryPixelRectangle: typeof YMaps.geometry.pixel.Rectangle,
    ) => {
      class BalloonPromoManager {
        public readonly objectManager: YMaps.ObjectManager;
        public pointType: EClustersType | null;

        private map: YMaps.Map;
        private rbush: RBush<IRBushItem>;
        private isDetailsVisible = false;

        public constructor(options: IBalloonPromoManagerOptions) {
          this.map = options.map;
          this.rbush = options.rbush;
          this.objectManager = new ymaps.ObjectManager();
          this.pointType = options.pointType;
        }

        public updateBalloons = (isPromoDetailsVisible?: boolean) => {
          this.isDetailsVisible = !!isPromoDetailsVisible;

          const bounds = this.map.getBounds();
          const rBushItems = this.rbush.search({
            minX: bounds[0][0],
            minY: bounds[0][1],
            maxX: bounds[1][0],
            maxY: bounds[1][1],
          });

          this.objectManager.removeAll();

          const features = rBushItems
            .map(this.prepareBalloon)
            .filter(feature => {
              if (!feature) {
                return false;
              }

              if (feature.properties.type === 'peas') {
                return false;
              }

              const isPulsed = !!feature.properties.isPulsedGlow;

              if (this.pointType) {
                return (
                  isPulsed && (this.pointType === EClustersType.Detailed || !!feature.properties.favoriteIds?.length)
                );
              }

              return isPulsed;
            })
            .sort((a, b) => {
              if (a?.properties.label && !b?.properties.label) {
                return -1;
              }
              if (!a?.properties.label && b?.properties.label) {
                return 1;
              }

              return 0;
            }) as TFeature[];

          this.objectManager.add({
            type: 'FeatureCollection',
            features,
          });
        };

        public updateBalloonProperties = (
          featureId: string,
          properties: TFeaturePropertiesRequiredPrice,
          isPromoDetailsVisible?: boolean,
        ) => {
          this.isDetailsVisible = !!isPromoDetailsVisible;

          this.objectManager.objects.setObjectOptions(featureId, {
            iconLayout: this.prepareBalloonLayout(properties as TFeaturePropertiesRequiredPrice),
            zIndex: properties.isHovered ? BALLOON_HOVERED_Z_INDEX : BALLOON_PROMO_Z_INDEX,
          });
        };

        private prepareBalloon = ({ feature }: IRBushItem) => {
          const { properties } = feature;

          if (!properties.minPrice && !properties.specialPromo) {
            return null;
          }

          return {
            ...feature,
            options: {
              iconLayout: this.prepareBalloonLayout(properties as TFeaturePropertiesRequiredPrice),
              zIndex: BALLOON_PROMO_Z_INDEX,
            },
          };
        };

        private prepareBalloonLayout = (properties: TFeaturePropertiesRequiredPrice) => {
          const content = this.prepareBalloonContent(properties);
          const pointType = this.pointType;

          return templateLayoutFactory.createClass(content, {
            getShape() {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const element: HTMLElement = (this as any).getElement();
              if (!element) {
                return null;
              }

              const wrapperElement = element.firstElementChild;
              if (!wrapperElement) {
                return null;
              }

              const boundingRect = wrapperElement.getBoundingClientRect();
              const offsetX = -boundingRect.width / 2;

              let shiftY = 6;

              if (!pointType) {
                shiftY = 4;
              } else {
                shiftY = pointType === EClustersType.OpenablePeas && properties.count > 1 ? 12 : 6;
              }

              const offsetY = -boundingRect.height - shiftY;

              return new shapeRectangle(
                new geometryPixelRectangle([
                  [offsetX, offsetY],
                  [offsetX + boundingRect.width, offsetY + boundingRect.height],
                ]),
              );
            },
          });
        };

        private prepareBalloonContent(feature: TFeaturePropertiesRequiredPrice) {
          const {
            count,
            minPrice,
            favoriteIds,
            hasNewobject,
            isAnyFromKp,
            isViewed,
            hasDiscountOffers,
            isActive,
            newbuilding,
            clusterOfferIds,
            details,
            isHovered,
          } = feature;

          const isClustersRedesign = Boolean(this.pointType);
          const isShowDetails = this.isDetailsVisible && !!details;
          const price = minPrice ? `${count > 1 ? 'от ' : ''}${commarize(minPrice)}` : '';
          const isFavorite = clusterOfferIds ? clusterOfferIds.some(id => favoriteIds?.includes(id)) : false;
          const iconMarkup = getIcon({
            isFavorite,
            isPromo: hasDiscountOffers,
            isActive,
            isViewed,
            isRedesign: isClustersRedesign,
            isPromoBalloon: true,
          });
          const label = feature?.label ? `<div class="${styles['label']}">${feature.label}</div>` : '';
          const newbuildingId = newbuilding?.id ?? 0;
          const isNewbuildingOnProPlus = !!newbuilding?.isAnyFicheringPlus || isAnyFromKp;
          const isCounter = count > 1;
          const countMarkup = isCounter
            ? `<div class="${classNames(styles['count'], {
                [styles['promotion']]: !isNewbuildingOnProPlus && !isFavorite && !hasNewobject,
              })}">${formatBalloonCount(count, isClustersRedesign ? 99 : undefined)}</div>`
            : '';
          const detailsText = isShowDetails
            ? `<div class="${classNames(styles['details'], {
                [styles['counter']]: isCounter,
              })}">${details}</div>`
            : '';

          return `
            <div
            data-testid="${isNewbuildingOnProPlus ? 'promo-pin' : 'promotion-pin'}"
              data-newbuilding="${newbuildingId}"
              class="${classNames(styles['promo-wrapper'], {
                [styles['promo-wrapper--redesign']]: isClustersRedesign,
                [styles['promo-wrapper--cluster']]: this.pointType === EClustersType.OpenablePeas && isCounter,
                [styles['promo-wrapper-details']]: isShowDetails,
                [styles['viewed']]: isViewed,
                [styles['counter-wrapper']]: !isClustersRedesign && isCounter,
              })}
            ">
              <div class="${classNames(styles['promo-balloon'], {
                [styles['residential-complex']]: hasNewobject || isAnyFromKp,
                [styles['colorized']]: isNewbuildingOnProPlus,
                [styles['active']]: isActive,
                [styles['promo-balloon--redesign']]: isClustersRedesign,
                [styles['promo-balloon--hovered']]: isHovered && !isActive,
              })}">
                <div class="${classNames(styles['content'], {
                  [styles['content--redesign']]: isClustersRedesign,
                })}">
                  ${label}
                  ${countMarkup}
                  <div class="${styles['price']}">${price}</div>
                  <div class="${styles['icon']}">${iconMarkup}</div>
                </div>
                ${detailsText}
              </div>
            </div>
          `;
        }
      }

      provide(BalloonPromoManager);
    },
  );
}
